import {
  ArrowRightLeft,
  Bug,
  CalendarCheck,
  CircleHelp,
  FileSearch,
  FolderCog,
  House,
  ListOrdered,
  ListTodo,
  MessagesSquare,
  PenLine,
  Sparkles,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { useState, type ReactElement } from "react";
import { Link, useLocation } from "react-router-dom";
import { heldBriefItems } from "@/components/grooming/post-all-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useGroomingBatch, useGroomingBatches, useProjectSession } from "@/hooks/queries/use-grooming";
import { useHelpInboxCount } from "@/hooks/queries/use-help-inbox";
import { useMe } from "@/hooks/queries/use-me";
import { isSetupPaused, pausedWaitingText } from "@/components/grooming/desktop-shell/queue-chip";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import { canAnywhere, canOn, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { localToday, readyToPostRun } from "@/lib/grooming/agenda-run";
import { GROOMING_QUEUE_PATH, groomingPath, isSharedAgendaPath, viewFromPath, type GroomingView } from "@/lib/grooming/paths";
import {
  ASK_PATH,
  ASK_TOOL_NAME,
  FOGBUGZ_FORMULATOR_PATH,
  FOGBUGZ_FORMULATOR_TOOL_NAME,
  FOGBUGZ_PATH,
  FOGBUGZ_TO_ADO_PATH,
  FOGBUGZ_TO_ADO_TOOL_NAME,
  FORMULATOR_PATH,
  ROLES_PATH,
  TOOLBOX_HOME_PATH,
  fogbugzToAdoVisible,
  isAskPath,
  isFogbugzFormulatorPath,
  isFogbugzPath,
  isFogbugzToAdoPath,
  rolesPath,
  toolFromPath,
} from "@/lib/toolbox/tools";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { useHelpPanelStore } from "@/stores/help-panel-store";
import { HELP_INBOX_PAGE_PATH } from "@/lib/toolbox/help";
import { useToolboxBridge } from "@/lib/toolbox/bridge";

interface RailItem {
  view: GroomingView;
  label: string;
  icon: LucideIcon;
}

const ITEMS: readonly RailItem[] = [
  { view: "backlog", label: "Backlog", icon: ListOrdered },
  { view: "session", label: "Session", icon: CalendarCheck },
  { view: "one", label: "One item", icon: Sparkles },
];

const QUEUE_ITEM: RailItem = { view: "queue", label: "Queue", icon: ListTodo };

/** The PM's project setup (PKG-PM-F, UX-PM-15): replaced the Repos item. */
const PROJECT_ITEM: RailItem = { view: "project", label: "Project", icon: FolderCog };

/**
 * The rail's views for a person: Queue (epic PI-SPEC-SW-E284) with
 * `read-backlog` on any project, since the queue is the person's own and
 * spans projects; Project only with `map-project` on the project (Project
 * Managers, Admins; E283; Repos before PKG-PM-F).
 */
export function railItemsFor(mayMapProject: boolean, mayReadQueue = false): readonly RailItem[] {
  return [...ITEMS, ...(mayReadQueue ? [QUEUE_ITEM] : []), ...(mayMapProject ? [PROJECT_ITEM] : [])];
}

/** Entries running or waiting in the run queue; 0 without a contract-3 app. */
export function useQueueActiveCount(): number {
  return useQueueBadge().count;
}

/**
 * The rail's Queue badge: how many entries run or wait, and whether the queue
 * waits for a setup fix (then the badge is amber, frame B2; epic
 * PI-SPEC-SW-E290, feature PI-SPEC-SW-1479).
 */
export function useQueueBadge(): { count: number; setupPaused: boolean } {
  const { state } = useGroomingQueue();
  return { count: state ? state.running.length + state.upNext.length : 0, setupPaused: isSetupPaused(state) };
}

/** A Formulator rail item (epic PI-SPEC-SW-E282): a fixed link, active while its path is open. */
interface FormulatorRailItem {
  label: string;
  icon: LucideIcon;
  href: string;
}

const FORMULATOR_RAIL_ITEMS: readonly FormulatorRailItem[] = [
  { label: "ADO Formulator", icon: PenLine, href: FORMULATOR_PATH },
];

/**
 * The FogBugz event log summary's rail item (epic PI-SPEC-SW-E307): shown in
 * both rails, after the tool's own items, with `fogbugz` on some project.
 * Without a saved FogBugz key its page says "Save your FogBugz key".
 */
export const FOGBUGZ_RAIL_ITEM = { label: "Event logs", title: "FogBugz event log summary", icon: FileSearch, href: FOGBUGZ_PATH } as const;

/**
 * The FogBugz Formulator's rail item (epic PI-SPEC-SW-E309, Resolved Decision
 * 1): shown in both rails, before "Event logs", with `fogbugz` on some
 * project. Without a saved FogBugz key its page says "Save your FogBugz key".
 */
export const FOGBUGZ_FORMULATOR_RAIL_ITEM = {
  label: FOGBUGZ_FORMULATOR_TOOL_NAME,
  title: FOGBUGZ_FORMULATOR_TOOL_NAME,
  icon: Bug,
  href: FOGBUGZ_FORMULATOR_PATH,
} as const;

/**
 * FogBugz to ADO's rail item (epic PI-SPEC-SW-E311, ER-1666 Package E): shown
 * in both rails, after the FogBugz items, while `fogbugzToAdoVisible`
 * (`fogbugz-to-ado` on some project and a saved FogBugz key).
 */
export const FOGBUGZ_TO_ADO_RAIL_ITEM = {
  label: FOGBUGZ_TO_ADO_TOOL_NAME,
  title: FOGBUGZ_TO_ADO_TOOL_NAME,
  icon: ArrowRightLeft,
  href: FOGBUGZ_TO_ADO_PATH,
} as const;

/**
 * Product Q&A's rail item (epic PI-SPEC-SW-E310, ER-1667 Package F): shown in
 * both rails, after the FogBugz items, with `ask` on some project.
 */
export const ASK_RAIL_ITEM = {
  label: ASK_TOOL_NAME,
  title: ASK_TOOL_NAME,
  icon: MessagesSquare,
  href: ASK_PATH,
} as const;

/**
 * Whether the rail's Home item is the active one: on Toolbox home itself
 * (`/toolbox`, `/toolbox/`) only, not on setup, Roles or the Formulator,
 * which also live under `/toolbox` (UX-PM-45). Pure; exported for tests.
 */
export function homeActive(pathname: string): boolean {
  return pathname === TOOLBOX_HOME_PATH || pathname === `${TOOLBOX_HOME_PATH}/`;
}

/**
 * Whether the rail shows the Formulator's items instead of Grooming's: on
 * Formulator pages, and on the Toolbox home and setup pages once `/me` says
 * `groomingVisible` is false (no Toolbox role anywhere; Grooming's views would
 * only say "No Toolbox role yet"). The same holds on the shared agenda, the
 * one grooming page a person with no role may open (PKG-PM-C, epic
 * PI-SPEC-SW-E291). While `/me` loads those pages keep Grooming's items, as
 * before.
 * Pure; exported for tests.
 */
export function railShowsFormulator(
  pathname: string,
  groomingVisible: boolean | undefined
): boolean {
  const tool = toolFromPath(pathname);
  if (tool === "formulator") return true;
  return (tool === "home" || isSharedAgendaPath(pathname)) && groomingVisible === false;
}

/**
 * How many briefs are ready to post for the project: exactly the N of the
 * Session screen's "Post all (N)". The run is the one the Session screen
 * works with (`readyToPostRun`: the newest prepared agenda run while it is
 * current and no draft agenda exists, the same shared definition), its count
 * is `heldBriefItems`, and Post all needs `manage-agenda` on the project.
 * 0 whenever the Session screen offers no Post all: no project, no such run,
 * a draft agenda, or no `manage-agenda`. Uses the existing reads.
 */
export function useReadyToPostCount(project: string | undefined): number {
  const { data: access } = useToolboxAccess();
  const mayPostAll = Boolean(project) && canOn(access, project, "manage-agenda");
  const batches = useGroomingBatches(mayPostAll ? project : undefined);
  const session = useProjectSession(mayPostAll ? project : undefined);
  const run = mayPostAll ? readyToPostRun(batches.data, session.data, localToday()) : undefined;
  const batch = useGroomingBatch(run?.id);
  return run && batch.data ? heldBriefItems(batch.data.items).length : 0;
}

/**
 * The Session item's tooltip (UX-PM-32): "N briefs ready to post" while the
 * badge shows, nothing otherwise. The Session link always sits inside it, so
 * the badge appearing or going away never remounts the link (focus stays).
 */
function ReadyToPostTooltip({ ready, children }: { ready: number; children: ReactElement }) {
  const [open, setOpen] = useState(false);
  return (
    <Tooltip open={ready > 0 && open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {ready > 0 && (
        <TooltipContent
          side="right"
          className="border border-[#27272A] bg-[#18181B] text-[#FAFAFA]"
          data-testid="ready-to-post-tooltip"
        >
          {readyToPostText(ready)}
        </TooltipContent>
      )}
    </Tooltip>
  );
}

/**
 * The Session badge's words (UX-PM-32): "1 brief ready to post" or
 * "N briefs ready to post". Pure; exported for tests.
 */
export function readyToPostText(count: number): string {
  return count === 1 ? "1 brief ready to post" : `${String(count)} briefs ready to post`;
}

/**
 * The Help badge's words for Admins: "1 open report in the Help inbox" or
 * "N open reports in the Help inbox". Pure; exported for tests.
 */
export function helpInboxOpenText(count: number): string {
  return count === 1 ? "1 open report in the Help inbox" : `${String(count)} open reports in the Help inbox`;
}

function pathFor(view: GroomingView, project: string | undefined): string {
  if (view === "queue") return GROOMING_QUEUE_PATH;
  if (!project) return view === "one" ? "/grooming/one" : "/grooming";
  return groomingPath(project, view);
}

const itemClassName =
  "relative flex h-14 w-16 flex-col items-center justify-center gap-[5px] rounded-[10px] text-[10.5px] font-medium transition-colors";

const activeClassName =
  "bg-[#1A1A1E] text-[#FAFAFA] before:absolute before:-left-1.5 before:bottom-4 before:top-4 before:w-[3px] before:rounded-r-[3px] before:bg-[#E11837] before:content-['']";
const idleClassName = `${TOOLBOX_TEXT.faint} hover:bg-white/[0.04] hover:text-[#FAFAFA]`;
/** The Help item while its panel is open (design `.ri.open`): lit, without the active view's red bar. */
const openClassName = "bg-[#1A1A1E] text-[#FAFAFA]";

/**
 * The Toolbox icon rail (design `.rail`): Home first on every Toolbox page
 * (both rails; active on Toolbox home, UX-PM-45), then the grooming views for
 * the person's role (with Queue and a badge of what is running or waiting in
 * it, epic PI-SPEC-SW-E284), Roles at the bottom for Admins and Project Managers
 * (`assign-roles` somewhere; PI-SPEC-SW-1431), then Help pinned last for
 * everyone, with or without a role (epic PI-SPEC-SW-E302, feature
 * PI-SPEC-SW-1557, ER-1655 Resolved Decisions 4 and 7: it opens and closes
 * the Toolbox Help panel, lit while the panel is open; F1 and Ctrl+/ do the
 * same from ToolboxFrame; for global Admins only, a badge with the open Help
 * inbox reports, refetched on window focus, feature PI-SPEC-SW-1559, frame
 * H7), a blue badge on
 * Session with the briefs ready to post (the same run and `heldBriefItems`
 * count as the Session screen's "Post all (N)") with a visible tooltip saying so
 * (UX-PM-32), and a red 3px bar on the active view.
 * On Formulator pages (epic PI-SPEC-SW-E282) it shows the Formulator's items
 * instead; see `railShowsFormulator`. Home, Roles and Help show in both rails,
 * and so do "FogBugz Formulator" (epic PI-SPEC-SW-E309) and "Event logs"
 * (the FogBugz event log summary, epic PI-SPEC-SW-E307) for a person with
 * `fogbugz` on some project, "FogBugz to ADO" (epic PI-SPEC-SW-E311)
 * for a person with `fogbugz-to-ado` on some project and a saved FogBugz key,
 * and "Product Q&A" (epic PI-SPEC-SW-E310) for a person with `ask` on some
 * project.
 */
export function IconRail({ project }: { project: string | undefined }) {
  const { pathname } = useLocation();
  const { data: me } = useMe();
  const showFormulator = railShowsFormulator(pathname, me?.groomingVisible);
  const ready = useReadyToPostCount(showFormulator ? undefined : project);
  const active = viewFromPath(pathname);
  const { data: access } = useToolboxAccess();
  const items = railItemsFor(canOn(access, project, "map-project"), canAnywhere(access, "read-backlog"));
  const { count: queued, setupPaused } = useQueueBadge();
  const mayAssignRoles = canAnywhere(access, "assign-roles");
  const mayFogbugz = canAnywhere(access, "fogbugz");
  const fogbugzActive = isFogbugzPath(pathname);
  const FogbugzIcon = FOGBUGZ_RAIL_ITEM.icon;
  const fogbugzFormulatorActive = isFogbugzFormulatorPath(pathname);
  const FogbugzFormulatorIcon = FOGBUGZ_FORMULATOR_RAIL_ITEM.icon;
  const bridge = useToolboxBridge();
  const mayBringToAdo = fogbugzToAdoVisible(canAnywhere(access, "fogbugz-to-ado"), me?.hasFogbugzKey ?? false, bridge);
  const fogbugzToAdoActive = isFogbugzToAdoPath(pathname);
  const FogbugzToAdoIcon = FOGBUGZ_TO_ADO_RAIL_ITEM.icon;
  const mayAsk = canAnywhere(access, "ask");
  const askActive = isAskPath(pathname);
  const AskIcon = ASK_RAIL_ITEM.icon;
  const rolesActive = pathname === ROLES_PATH || pathname.startsWith(`${ROLES_PATH}/`);
  const isHome = homeActive(pathname);
  const helpOpen = useHelpPanelStore((state) => state.open);
  const toggleHelp = useHelpPanelStore((state) => state.toggle);
  const isGlobalAdmin = me?.isGlobalAdmin === true;
  const { data: helpInboxOpen = 0 } = useHelpInboxCount(isGlobalAdmin);
  // The Help inbox page is Help's own page (frame H7): the Help item is the active one there.
  const helpInboxActive = pathname === HELP_INBOX_PAGE_PATH || pathname.startsWith(`${HELP_INBOX_PAGE_PATH}/`);

  return (
    <TooltipProvider delayDuration={200}>
      <nav
        aria-label={showFormulator ? "ADO Formulator views" : "Grooming views"}
        className="flex w-[76px] flex-none flex-col items-center gap-1 border-r border-[#1F1F23] bg-[#0B0B0D] py-2.5"
        data-testid="icon-rail"
      >
        <Link
          to={TOOLBOX_HOME_PATH}
          aria-current={isHome ? "page" : undefined}
          className={cn(itemClassName, isHome ? activeClassName : idleClassName)}
          data-testid="rail-home"
        >
          <House className="h-5 w-5" aria-hidden="true" />
          <span>Home</span>
        </Link>
        {showFormulator &&
          FORMULATOR_RAIL_ITEMS.map((item) => {
            const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                aria-current={isActive ? "page" : undefined}
                className={cn(itemClassName, isActive ? activeClassName : idleClassName)}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
                <span className="text-center leading-[1.1]">{item.label}</span>
              </Link>
            );
          })}
        {!showFormulator &&
          items.map((item) => {
            const isActive = active === item.view;
            const Icon = item.icon;
            const badged = item.view === "session" && ready > 0;
            const link = (
              <Link
                key={item.view}
                to={pathFor(item.view, project)}
                aria-current={isActive ? "page" : undefined}
                className={cn(itemClassName, isActive ? activeClassName : idleClassName)}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
                <span className="text-center leading-[1.1]">{item.label}</span>
                {badged && (
                  <span
                    className="absolute right-3 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#1D4ED8] px-1 text-[10px] font-semibold text-white"
                    aria-label={readyToPostText(ready)}
                    data-testid="ready-to-post-badge"
                  >
                    {ready}
                  </span>
                )}
                {item.view === "queue" && queued > 0 && (
                  <span
                    className={cn(
                      "absolute right-3 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold",
                      setupPaused ? "bg-amber-500/20 text-[#FBBF24]" : "bg-[#3F3F46] text-white"
                    )}
                    aria-label={setupPaused ? pausedWaitingText(queued) : `${String(queued)} in the queue`}
                    data-testid="queue-count-badge"
                    data-tone={setupPaused ? "amber" : "normal"}
                  >
                    {queued}
                  </span>
                )}
              </Link>
            );
            if (item.view !== "session") return link;
            return (
              <ReadyToPostTooltip key={item.view} ready={ready}>
                {link}
              </ReadyToPostTooltip>
            );
          })}
        {mayFogbugz && (
          <Link
            to={FOGBUGZ_FORMULATOR_RAIL_ITEM.href}
            title={FOGBUGZ_FORMULATOR_RAIL_ITEM.title}
            aria-current={fogbugzFormulatorActive ? "page" : undefined}
            className={cn(itemClassName, fogbugzFormulatorActive ? activeClassName : idleClassName)}
            data-testid="rail-fogbugz-formulator"
          >
            <FogbugzFormulatorIcon className="h-5 w-5" aria-hidden="true" />
            <span className="text-center leading-[1.1]">{FOGBUGZ_FORMULATOR_RAIL_ITEM.label}</span>
          </Link>
        )}
        {mayFogbugz && (
          <Link
            to={FOGBUGZ_RAIL_ITEM.href}
            title={FOGBUGZ_RAIL_ITEM.title}
            aria-current={fogbugzActive ? "page" : undefined}
            className={cn(itemClassName, fogbugzActive ? activeClassName : idleClassName)}
            data-testid="rail-fogbugz"
          >
            <FogbugzIcon className="h-5 w-5" aria-hidden="true" />
            <span className="text-center leading-[1.1]">{FOGBUGZ_RAIL_ITEM.label}</span>
          </Link>
        )}
        {mayBringToAdo && (
          <Link
            to={FOGBUGZ_TO_ADO_RAIL_ITEM.href}
            title={FOGBUGZ_TO_ADO_RAIL_ITEM.title}
            aria-current={fogbugzToAdoActive ? "page" : undefined}
            className={cn(itemClassName, fogbugzToAdoActive ? activeClassName : idleClassName)}
            data-testid="rail-fogbugz-to-ado"
          >
            <FogbugzToAdoIcon className="h-5 w-5" aria-hidden="true" />
            <span className="text-center leading-[1.1]">{FOGBUGZ_TO_ADO_RAIL_ITEM.label}</span>
          </Link>
        )}
        {mayAsk && (
          <Link
            to={ASK_RAIL_ITEM.href}
            title={ASK_RAIL_ITEM.title}
            aria-current={askActive ? "page" : undefined}
            className={cn(itemClassName, askActive ? activeClassName : idleClassName)}
            data-testid="rail-ask"
          >
            <AskIcon className="h-5 w-5" aria-hidden="true" />
            <span className="text-center leading-[1.1]">{ASK_RAIL_ITEM.label}</span>
          </Link>
        )}
        <span className="flex-1" />
        {mayAssignRoles && (
          <Link
            to={rolesPath(project && canOn(access, project, "assign-roles") ? project : undefined)}
            aria-current={rolesActive ? "page" : undefined}
            className={cn(itemClassName, rolesActive ? activeClassName : idleClassName)}
            data-testid="rail-roles"
          >
            <UsersRound className="h-5 w-5" aria-hidden="true" />
            <span>Roles</span>
          </Link>
        )}
        <button
          type="button"
          onClick={toggleHelp}
          aria-pressed={helpOpen}
          aria-controls="toolbox-help-panel"
          title="Help (F1)"
          aria-current={helpInboxActive ? "page" : undefined}
          className={cn(
            itemClassName,
            helpInboxActive ? activeClassName : helpOpen ? openClassName : idleClassName
          )}
          data-testid="rail-help"
          data-open={helpOpen ? "true" : "false"}
        >
          <CircleHelp className="h-5 w-5" aria-hidden="true" />
          <span>Help</span>
          {isGlobalAdmin && helpInboxOpen > 0 && (
            <span
              className="absolute right-3 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#1D4ED8] px-1 text-[10px] font-semibold text-white"
              aria-label={helpInboxOpenText(helpInboxOpen)}
              data-testid="help-inbox-count-badge"
            >
              {helpInboxOpen}
            </span>
          )}
        </button>
      </nav>
    </TooltipProvider>
  );
}
